(function () {
  'use strict';

  const State = window.RFAWishlistState;
  const configElement = document.getElementById('rfa-wishlist-config');
  if (!State || !configElement) return;

  const TOKEN_KEY = 'rfa_wishlist_oauth_token';
  const PKCE_KEY = 'rfa_wishlist_pkce';
  const BUCKET_NAMESPACE = 'rfa';
  const CARD_CACHE_PREFIX = 'rfa-wishlist-card:v1:';
  const GRID_SNAPSHOT_PREFIX = 'rf-infinite-product-grid:v';
  const WISHLIST_WINDOW_PREFIX = 'rfa-wishlist-window:v';
  const CARD_RENDER_CONCURRENCY = navigator.connection?.saveData ? 8 : 24;
  const config = JSON.parse(configElement.textContent || '{}');
  const savedIds = new Set((config.initialProductIds || []).filter(State.isProductGid));
  const statusElement = document.getElementById('rfa-wishlist-status');
  const authDialog = document.getElementById('rfa-wishlist-auth-dialog');
  const authDialogTransitionDuration = 133;
  let discoveryPromise;
  let snapshotCache = null;
  let wishlistInfiniteState = null;
  let wishlistWindowPersistTimer = null;
  let authorizationInProgress = false;
  let authDialogOpener = null;
  let authDialogCloseTimer = null;
  let mutationQueue = Promise.resolve();
  const confirmedIds = new Set(savedIds);
  const pendingMutations = [];

  const WISHLIST_QUERY = `
    query RfaWishlist {
      customer {
        id
        wishlist01: metafield(namespace: "rfa", key: "wishlist_01") { value compareDigest }
        wishlist02: metafield(namespace: "rfa", key: "wishlist_02") { value compareDigest }
        wishlist03: metafield(namespace: "rfa", key: "wishlist_03") { value compareDigest }
        wishlist04: metafield(namespace: "rfa", key: "wishlist_04") { value compareDigest }
        wishlist05: metafield(namespace: "rfa", key: "wishlist_05") { value compareDigest }
        wishlist06: metafield(namespace: "rfa", key: "wishlist_06") { value compareDigest }
        wishlist07: metafield(namespace: "rfa", key: "wishlist_07") { value compareDigest }
        wishlist08: metafield(namespace: "rfa", key: "wishlist_08") { value compareDigest }
      }
    }
  `;

  const WISHLIST_MUTATION = `
    mutation RfaWishlistSet($metafields: [MetafieldsSetInput!]!) {
      metafieldsSet(metafields: $metafields) {
        metafields { key value compareDigest }
        userErrors { field message code }
      }
    }
  `;

  const STOREFRONT_RESOLVE_QUERY = `
    query ResolveWishlistProductIds($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product { id handle }
      }
    }
  `;

  function showStatus(message, isError = false) {
    if (!statusElement) return;
    statusElement.textContent = message;
    statusElement.classList.toggle('is-error', isError);
    statusElement.classList.add('is-visible');
    window.clearTimeout(showStatus.timeout);
    showStatus.timeout = window.setTimeout(() => statusElement.classList.remove('is-visible'), 6000);
  }

  function currentReturnUrl() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  function safeReturnUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : config.wishlistUrl;
    } catch (_error) {
      return config.wishlistUrl;
    }
  }

  function accountEntryUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin === window.location.origin) url.searchParams.set('return_url', currentReturnUrl());
      return url.toString();
    } catch (_error) {
      return value;
    }
  }

  function openAccountPrompt() {
    if (!authDialog) return;
    const loginLink = authDialog.querySelector('[data-rfa-wishlist-login-link]');
    if (loginLink) loginLink.href = accountEntryUrl(config.loginUrl);

    if (authDialog.open) return;
    authDialogOpener = document.activeElement;
    if (typeof authDialog.showModal === 'function') authDialog.showModal();
    else authDialog.setAttribute('open', '');
    window.setTimeout(() => authDialog.classList.add('is-open'), 20);
  }

  function closeAccountPrompt() {
    if (!authDialog?.open) return;
    authDialog.classList.remove('is-open');
    window.clearTimeout(authDialogCloseTimer);
    authDialogCloseTimer = window.setTimeout(() => {
      if (typeof authDialog.close === 'function') authDialog.close();
      else authDialog.removeAttribute('open');
      authDialogOpener?.focus?.({ preventScroll: true });
    }, authDialogTransitionDuration);
  }

  function handleAccountPromptClick(event) {
    const loginLink = event.target.closest('[data-rfa-wishlist-login-link]');
    if (loginLink) {
      event.preventDefault();
      startAuthorization(currentReturnUrl());
      return;
    }

    if (event.target === authDialog || event.target.closest('[data-rfa-wishlist-auth-close]')) {
      closeAccountPrompt();
    }
  }

  function randomBase64Url(byteLength) {
    const bytes = new Uint8Array(byteLength);
    window.crypto.getRandomValues(bytes);
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  async function sha256Base64Url(value) {
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    let binary = '';
    new Uint8Array(digest).forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  async function discover() {
    if (!discoveryPromise) {
      discoveryPromise = Promise.all([
        fetch(`${window.location.origin}/.well-known/openid-configuration`, { headers: { Accept: 'application/json' } }),
        fetch(`${window.location.origin}/.well-known/customer-account-api`, { headers: { Accept: 'application/json' } }),
      ]).then(async ([oauthResponse, apiResponse]) => {
        if (!oauthResponse.ok || !apiResponse.ok) throw new Error('Shopify Customer Account discovery failed.');
        const [oauth, api] = await Promise.all([oauthResponse.json(), apiResponse.json()]);
        return { ...oauth, ...api };
      });
    }
    return discoveryPromise;
  }

  function loadToken() {
    try {
      return JSON.parse(window.sessionStorage.getItem(TOKEN_KEY) || 'null');
    } catch (_error) {
      return null;
    }
  }

  function saveToken(payload) {
    const previous = loadToken();
    const token = {
      accessToken: payload.access_token,
      refreshToken: payload.refresh_token || previous?.refreshToken || null,
      expiresAt: Date.now() + (Number(payload.expires_in || 300) * 1000),
    };
    window.sessionStorage.setItem(TOKEN_KEY, JSON.stringify(token));
    return token;
  }

  function clearAuth() {
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(PKCE_KEY);
  }

  async function tokenRequest(parameters) {
    const discovery = await discover();
    const response = await fetch(discovery.token_endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(parameters),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.access_token) {
      const error = new Error(payload.error_description || payload.error || 'Shopify authorization failed.');
      error.code = payload.error || 'OAUTH_ERROR';
      throw error;
    }
    return saveToken(payload);
  }

  async function getAccessToken() {
    const token = loadToken();
    if (!token) return null;
    if (token.accessToken && token.expiresAt > Date.now() + 30000) return token.accessToken;
    if (!token.refreshToken) return null;

    try {
      const refreshed = await tokenRequest({
        grant_type: 'refresh_token',
        refresh_token: token.refreshToken,
        client_id: config.clientId,
      });
      return refreshed.accessToken;
    } catch (_error) {
      clearAuth();
      return null;
    }
  }

  function isWishlistMutation(value) {
    return State.isProductGid(value?.productId) && (value.action === 'add' || value.action === 'remove');
  }

  function normalizeRestorePosition(value) {
    const position = value?.restorePosition;
    if (!position) return null;

    const originalIndex = Number(position.originalIndex);
    return {
      previousProductId: State.isProductGid(position.previousProductId) ? position.previousProductId : null,
      nextProductId: State.isProductGid(position.nextProductId) ? position.nextProductId : null,
      originalIndex: Number.isInteger(originalIndex) && originalIndex >= 0 ? originalIndex : null,
    };
  }

  function startAuthorization(returnUrl, pendingMutation = null) {
    if (authorizationInProgress) return;
    authorizationInProgress = true;
    beginAuthorization(returnUrl, pendingMutation).catch((error) => {
      authorizationInProgress = false;
      showStatus(error.message || 'Die Anmeldung für die Wunschliste konnte nicht gestartet werden.', true);
    });
  }

  async function beginAuthorization(returnUrl, pendingMutation = null) {
    const discovery = await discover();
    const verifier = randomBase64Url(64);
    const challenge = await sha256Base64Url(verifier);
    const state = randomBase64Url(32);
    const nonce = randomBase64Url(32);

    window.sessionStorage.setItem(PKCE_KEY, JSON.stringify({
      verifier,
      state,
      returnUrl: safeReturnUrl(returnUrl),
      pendingMutation: isWishlistMutation(pendingMutation)
        ? {
          productId: pendingMutation.productId,
          action: pendingMutation.action,
          restorePosition: normalizeRestorePosition(pendingMutation),
        }
        : null,
      createdAt: Date.now(),
    }));

    const authorizationUrl = new URL(discovery.authorization_endpoint);
    authorizationUrl.searchParams.set('scope', 'openid email customer-account-api:full');
    authorizationUrl.searchParams.set('client_id', config.clientId);
    authorizationUrl.searchParams.set('response_type', 'code');
    authorizationUrl.searchParams.set('redirect_uri', config.callbackUri);
    authorizationUrl.searchParams.set('state', state);
    authorizationUrl.searchParams.set('nonce', nonce);
    authorizationUrl.searchParams.set('code_challenge', challenge);
    authorizationUrl.searchParams.set('code_challenge_method', 'S256');
    window.location.assign(authorizationUrl.toString());
  }

  async function processAuthorizationCallback() {
    const parameters = new URLSearchParams(window.location.search);
    if (!parameters.has('code') && !parameters.has('error')) return false;

    const pkce = JSON.parse(window.sessionStorage.getItem(PKCE_KEY) || 'null');
    const returnUrl = safeReturnUrl(pkce?.returnUrl);
    window.history.replaceState({}, '', returnUrl);

    if (parameters.has('error')) {
      clearAuth();
      showStatus(parameters.get('error_description') || 'Die Anmeldung für die Wunschliste wurde abgebrochen.', true);
      return true;
    }

    if (!pkce || pkce.state !== parameters.get('state') || Date.now() - pkce.createdAt > 10 * 60 * 1000) {
      clearAuth();
      showStatus('Die sichere Anmeldung für die Wunschliste ist abgelaufen. Bitte versuchen Sie es erneut.', true);
      return true;
    }

    try {
      await tokenRequest({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        code: parameters.get('code'),
        redirect_uri: config.callbackUri,
        code_verifier: pkce.verifier,
      });
      window.sessionStorage.removeItem(PKCE_KEY);
    } catch (error) {
      clearAuth();
      showStatus(error.message || 'Die Anmeldung für die Wunschliste ist fehlgeschlagen.', true);
      return true;
    }

    if (isWishlistMutation(pkce.pendingMutation)) {
      try {
        const result = await mutate(
          pkce.pendingMutation.productId,
          pkce.pendingMutation.action,
          normalizeRestorePosition(pkce.pendingMutation)
        );
        const persistedIds = State.flattenBuckets(result.buckets || result.snapshot.buckets);
        setConfirmedWishlistState(persistedIds);
        publishWishlistState();
        if (result.capacityReached) {
          showStatus('Die Wunschliste ist voll. Es können maximal 1.024 Werke gespeichert werden.', true);
        }
      } catch (error) {
        showStatus(error.message || 'Die Wunschliste konnte nach der Anmeldung nicht aktualisiert werden.', true);
      }
    }

    if (returnUrl !== config.wishlistUrl) {
      window.location.replace(returnUrl);
      return true;
    }

    return true;
  }

  async function customerGraphql(query, variables = {}) {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      const error = new Error('Customer authorization is required.');
      error.code = 'UNAUTHORIZED';
      throw error;
    }

    const discovery = await discover();
    const response = await fetch(discovery.graphql_api, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: accessToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, variables }),
    });
    const payload = await response.json().catch(() => ({}));

    if (response.status === 401) {
      clearAuth();
      const error = new Error('Customer authorization expired.');
      error.code = 'UNAUTHORIZED';
      throw error;
    }
    if (!response.ok || payload.errors?.length) {
      throw new Error(payload.errors?.[0]?.message || `Customer Account API request failed (${response.status}).`);
    }
    return payload.data;
  }

  async function storefrontGraphql(query, variables = {}) {
    const response = await fetch(config.storefrontApiUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.storefrontAccessToken,
      },
      body: JSON.stringify({ query, variables }),
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok || payload.errors?.length) {
      throw new Error(payload.errors?.[0]?.message || `Storefront API request failed (${response.status}).`);
    }
    return payload.data;
  }

  function chunks(items, size) {
    const result = [];
    for (let index = 0; index < items.length; index += size) {
      result.push(items.slice(index, index + size));
    }
    return result;
  }

  async function resolveStorefrontProducts(productIds) {
    if (!productIds.length) return new Map();
    const responses = await Promise.all(
      chunks(productIds, 250).map((ids) => storefrontGraphql(STOREFRONT_RESOLVE_QUERY, { ids }))
    );
    return new Map(
      responses
        .flatMap((data) => data?.nodes || [])
        .filter((product) => State.isProductGid(product?.id) && product?.handle)
        .map((product) => [product.id, product])
    );
  }

  async function readSnapshot() {
    const data = await customerGraphql(WISHLIST_QUERY);
    if (!data?.customer?.id) throw new Error('Shopify did not return the signed-in customer.');
    snapshotCache = State.snapshotFromApi(data.customer);
    return snapshotCache;
  }

  async function writeSnapshot(snapshot, result) {
    const metafields = result.buckets.flatMap((bucket, index) => {
      const previousBucket = snapshot.buckets[index] || [];
      const bucketChanged = previousBucket.length !== bucket.length
        || previousBucket.some((productId, productIndex) => productId !== bucket[productIndex]);

      if (!snapshot.dirty && !bucketChanged) return [];

      return [{
        ownerId: snapshot.ownerId,
        namespace: BUCKET_NAMESPACE,
        key: `wishlist_${String(index + 1).padStart(2, '0')}`,
        type: 'list.product_reference',
        value: JSON.stringify(bucket),
        compareDigest: snapshot.digests[index],
      }];
    });
    const data = await customerGraphql(WISHLIST_MUTATION, { metafields });
    const userError = data?.metafieldsSet?.userErrors?.[0];
    if (userError) {
      const error = new Error(userError.message);
      error.code = userError.code;
      if (State.isCasConflict(error)) snapshotCache = null;
      throw error;
    }

    const nextDigests = [...snapshot.digests];
    (data?.metafieldsSet?.metafields || []).forEach((metafield) => {
      const bucketNumber = Number.parseInt(String(metafield.key || '').replace('wishlist_', ''), 10);
      if (bucketNumber >= 1 && bucketNumber <= State.BUCKET_COUNT) {
        nextDigests[bucketNumber - 1] = metafield.compareDigest;
      }
    });
    snapshotCache = {
      ownerId: snapshot.ownerId,
      buckets: result.buckets.map((bucket) => [...bucket]),
      digests: nextDigests,
      dirty: false,
    };
  }

  function setSavedState(ids) {
    savedIds.clear();
    ids.forEach((id) => savedIds.add(id));
    updateButtons(document);
    const count = document.querySelector('[data-rfa-wishlist-count]');
    if (count) count.textContent = String(savedIds.size);
    const empty = document.querySelector('[data-rfa-wishlist-empty]');
    if (empty && savedIds.size > 0) empty.hidden = true;
  }

  function visibleWishlistIds() {
    const ids = new Set(confirmedIds);
    pendingMutations.forEach(({ productId, action }) => {
      if (action === 'add') ids.add(productId);
      else ids.delete(productId);
    });
    return ids;
  }

  function publishWishlistState() {
    setSavedState(visibleWishlistIds());
  }

  function setConfirmedWishlistState(ids) {
    confirmedIds.clear();
    ids.forEach((id) => confirmedIds.add(id));
  }

  function settleMutation(mutation, ids = null) {
    if (ids) setConfirmedWishlistState(ids);
    const index = pendingMutations.indexOf(mutation);
    if (index !== -1) pendingMutations.splice(index, 1);
    publishWishlistState();
  }

  function updateButtons(root) {
    root.querySelectorAll?.('[data-rfa-wishlist-product-id]').forEach((button) => {
      const isSaved = savedIds.has(button.dataset.rfaWishlistProductId);
      button.classList.toggle('is-on', isSaved);
      button.setAttribute('aria-pressed', String(isSaved));
      button.setAttribute('aria-label', isSaved ? 'Aus der Wunschliste entfernen' : 'Zur Wunschliste hinzufügen');
    });
  }

  function wishlistCardForProduct(productId) {
    const selector = `[data-rfa-wishlist-card][data-product-id="${CSS.escape(productId)}"]`;
    return document.querySelector(selector);
  }

  function showRemovedWishlistCard(card, productId, productIndex, productIds) {
    const frame = card.querySelector('.rf-card__framewrap');
    if (!frame || card.classList.contains('rfa-wishlist-card--removed')) return false;

    const previousProductId = productIds[productIndex - 1] || '';
    const nextProductId = productIds[productIndex + 1] || '';
    const mediaLink = frame.querySelector('.rf-card__media-link');
    const saveButton = frame.querySelector('[data-rfa-wishlist-control]');
    const notice = document.createElement('div');
    const message = document.createElement('p');
    const undoButton = document.createElement('button');

    card.classList.add('rfa-wishlist-card--removed');
    card.dataset.rfaWishlistRemovedIndex = String(productIndex);
    card.dataset.rfaWishlistPreviousProductId = previousProductId;
    card.dataset.rfaWishlistNextProductId = nextProductId;

    if (mediaLink) {
      mediaLink.inert = true;
      mediaLink.setAttribute('aria-hidden', 'true');
      mediaLink.setAttribute('tabindex', '-1');
    }
    if (saveButton) {
      saveButton.inert = true;
      saveButton.setAttribute('tabindex', '-1');
    }

    notice.className = 'rfa-wishlist-removed-card__notice';
    notice.setAttribute('role', 'status');
    message.className = 'rfa-wishlist-removed-card__message';
    message.textContent = 'Dieses Werk wurde von Ihrer Wunschliste entfernt.';
    undoButton.type = 'button';
    undoButton.className = 'rfa-wishlist-removed-card__undo';
    undoButton.textContent = 'Rückgängig';
    undoButton.dataset.rfaWishlistUndo = '';
    undoButton.dataset.rfaWishlistProductId = productId;
    notice.append(message, undoButton);
    frame.append(notice);
    undoButton.focus({ preventScroll: true });
    return true;
  }

  function restoreRemovedWishlistCard(productId) {
    const card = wishlistCardForProduct(productId);
    if (!card?.classList.contains('rfa-wishlist-card--removed')) return false;

    const restorePosition = {
      previousProductId: card.dataset.rfaWishlistPreviousProductId,
      nextProductId: card.dataset.rfaWishlistNextProductId,
      originalIndex: card.dataset.rfaWishlistRemovedIndex,
    };
    const state = wishlistInfiniteState;
    if (state && !state.productIds.includes(productId)) {
      const nextProductIndex = state.productIds.indexOf(restorePosition.nextProductId);
      const previousProductIndex = state.productIds.indexOf(restorePosition.previousProductId);
      const originalProductIndex = Number.parseInt(restorePosition.originalIndex, 10);
      const productIndex = nextProductIndex !== -1
        ? nextProductIndex
        : (previousProductIndex !== -1 ? previousProductIndex + 1 : Math.min(
          Number.isInteger(originalProductIndex) ? originalProductIndex : state.productIds.length,
          state.productIds.length
        ));

      state.productIds.splice(productIndex, 0, productId);
      if (productIndex <= state.nextOffset) state.nextOffset += 1;
      state.controller.setClientHasNext(state.nextOffset < state.productIds.length);
      scheduleWishlistWindowPersist();
    }

    const frame = card.querySelector('.rf-card__framewrap');
    const mediaLink = frame?.querySelector('.rf-card__media-link');
    const saveButton = frame?.querySelector('[data-rfa-wishlist-control]');
    frame?.querySelector('.rfa-wishlist-removed-card__notice')?.remove();
    card.classList.remove('rfa-wishlist-card--removed');
    delete card.dataset.rfaWishlistRemovedIndex;
    delete card.dataset.rfaWishlistPreviousProductId;
    delete card.dataset.rfaWishlistNextProductId;
    if (mediaLink) {
      mediaLink.inert = false;
      mediaLink.removeAttribute('aria-hidden');
      mediaLink.removeAttribute('tabindex');
    }
    if (saveButton) {
      saveButton.inert = false;
      saveButton.removeAttribute('tabindex');
    }
    return restorePosition;
  }

  function removeWishlistCard(productId) {
    const card = wishlistCardForProduct(productId);
    if (!card || card.classList.contains('rfa-wishlist-card--removed')) return;

    const state = wishlistInfiniteState;
    const productIndex = state?.productIds.indexOf(productId) ?? -1;
    if (state && productIndex !== -1) {
      const productIds = [...state.productIds];
      state.productIds.splice(productIndex, 1);
      if (productIndex < state.nextOffset) state.nextOffset -= 1;
      state.controller.setClientHasNext(state.nextOffset < state.productIds.length);
      scheduleWishlistWindowPersist();
      if (showRemovedWishlistCard(card, productId, productIndex, productIds)) return;
    }

    const controller = card.closest('infinite-product-grid');
    if (!controller?.removeClientSlot?.(card)) card.remove();

    const grid = document.querySelector('[data-rfa-wishlist-grid]');
    const empty = document.querySelector('[data-rfa-wishlist-empty]');
    if (grid && !grid.querySelector('[data-rfa-wishlist-card]')) {
      if (state && state.nextOffset < state.productIds.length) {
        grid.hidden = false;
        if (empty) empty.hidden = true;
        loadNextWishlistBatch();
      } else {
        grid.hidden = true;
        if (empty) empty.hidden = false;
      }
    }
  }

  async function syncFromApi(cleanDeleted = false) {
    const result = await State.runCas({
      read: readSnapshot,
      transform: async (snapshot) => ({
        buckets: snapshot.buckets,
        changed: cleanDeleted && snapshot.dirty,
      }),
      write: writeSnapshot,
      maxRetries: 3,
    });
    setConfirmedWishlistState(State.flattenBuckets(result.buckets || result.snapshot.buckets));
    publishWishlistState();
  }

  async function mutate(productId, action, restorePosition = null) {
    return State.runCas({
      read: async () => snapshotCache || readSnapshot(),
      transform: async (snapshot) => {
        const operation = State.applyOperation(snapshot.buckets, productId, action, restorePosition);
        return { ...operation, changed: operation.changed || snapshot.dirty };
      },
      write: writeSnapshot,
      maxRetries: 3,
    });
  }

  async function removeMissingProducts(productIds) {
    const missingIds = new Set(productIds);
    if (!missingIds.size) return;

    const result = await State.runCas({
      read: async () => snapshotCache || readSnapshot(),
      transform: async (snapshot) => {
        const cleanup = State.removeProducts(snapshot.buckets, missingIds);
        return { ...cleanup, changed: cleanup.changed || snapshot.dirty };
      },
      write: writeSnapshot,
      maxRetries: 3,
    });
    setConfirmedWishlistState(State.flattenBuckets(result.buckets || result.snapshot.buckets));
    publishWishlistState();
  }

  function storefrontProductUrl(product) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}products/${encodeURIComponent(product.handle)}`;
  }

  function wishlistWindowStorageKey() {
    try {
      const url = new URL(config.wishlistUrl, window.location.origin);
      return `rfa-wishlist-window:v1:${url.pathname}`;
    } catch (_error) {
      return null;
    }
  }

  function wishlistRestoreIntentKey() {
    const key = wishlistWindowStorageKey();
    return key ? `${key}:return` : null;
  }

  function hasWishlistRestoreIntent() {
    const key = wishlistRestoreIntentKey();
    if (!key) return false;

    try {
      return window.sessionStorage.getItem(key) === 'product';
    } catch (_error) {
      return false;
    }
  }

  function markWishlistRestoreIntent() {
    const key = wishlistRestoreIntentKey();
    if (!key) return;

    try {
      window.sessionStorage.setItem(key, 'product');
    } catch (_error) {
      // Scroll restoration remains optional when browser storage is unavailable.
    }
  }

  function clearStoredGridPositions() {
    try {
      const keys = [];
      for (let index = 0; index < window.sessionStorage.length; index += 1) {
        const key = window.sessionStorage.key(index);
        if (key?.startsWith(GRID_SNAPSHOT_PREFIX) || key?.startsWith(WISHLIST_WINDOW_PREFIX)) keys.push(key);
      }
      keys.forEach((key) => window.sessionStorage.removeItem(key));
    } catch (_error) {
      // No stored position needs clearing when storage is unavailable.
    }
  }

  function isPrimaryNavigationClick(event) {
    return !(
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    );
  }

  function handleGridNavigation(event) {
    if (!isPrimaryNavigationClick(event)) return;

    const link = event.target.closest('a[href]');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

    try {
      const targetUrl = new URL(link.href, window.location.origin);
      const currentUrl = new URL(window.location.href);
      if (targetUrl.origin !== window.location.origin) return;
      if (targetUrl.pathname === currentUrl.pathname && targetUrl.search === currentUrl.search) return;

      if (targetUrl.pathname.includes('/products/')) {
        if (link.closest('[data-rfa-wishlist-page]')) markWishlistRestoreIntent();
        return;
      }
    } catch (_error) {
      return;
    }

    clearStoredGridPositions();
  }

  function readWishlistWindow(productIds, perPage) {
    if (!hasWishlistRestoreIntent()) return null;

    const key = wishlistWindowStorageKey();
    if (!key) return null;

    try {
      const windowState = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      const {
        page,
        startOffset,
        productIds: savedWindowIds,
        renderThroughPage = page,
        renderThroughProductIds = savedWindowIds,
      } = windowState || {};
      if (
        ![1, 2].includes(windowState?.version)
        || !Number.isInteger(page)
        || page < 1
        || !Number.isInteger(startOffset)
        || startOffset < 0
        || !Array.isArray(savedWindowIds)
        || !savedWindowIds.length
        || !Number.isInteger(renderThroughPage)
        || renderThroughPage < page
        || !Array.isArray(renderThroughProductIds)
        || !renderThroughProductIds.length
      ) return null;

      const currentWindowIds = productIds.slice(startOffset, startOffset + savedWindowIds.length);
      if (currentWindowIds.length !== savedWindowIds.length) return null;
      if (currentWindowIds.some((productId, index) => productId !== savedWindowIds[index])) return null;
      const renderThroughStartOffset = (renderThroughPage - 1) * perPage;
      const currentRenderThroughIds = productIds.slice(
        renderThroughStartOffset,
        renderThroughStartOffset + renderThroughProductIds.length
      );
      if (currentRenderThroughIds.length !== renderThroughProductIds.length) return null;
      if (currentRenderThroughIds.some((productId, index) => productId !== renderThroughProductIds[index])) return null;
      return {
        page,
        startOffset,
        productIds: currentWindowIds,
        renderThroughPage,
        renderThroughStartOffset,
        renderThroughProductIds: currentRenderThroughIds,
      };
    } catch (_error) {
      return null;
    }
  }

  function persistWishlistWindow() {
    const state = wishlistInfiniteState;
    const key = wishlistWindowStorageKey();
    const currentRecord = state?.controller.records?.get(state.controller.currentPage);
    if (!key || !currentRecord?.slots.length) return;

    if (!hasWishlistRestoreIntent()) {
      clearWishlistRestoreState(state.controller);
      return;
    }

    const startOffset = (currentRecord.page - 1) * state.perPage;
    const productIds = state.productIds.slice(startOffset, startOffset + currentRecord.slots.length);
    if (!productIds.length) return;
    const renderThroughPage = Math.max(
      currentRecord.page,
      state.controller.visibleThroughPage?.() || currentRecord.page
    );
    const renderThroughRecord = state.controller.records.get(renderThroughPage);
    const renderThroughStartOffset = (renderThroughPage - 1) * state.perPage;
    const renderThroughProductIds = state.productIds.slice(
      renderThroughStartOffset,
      renderThroughStartOffset + (renderThroughRecord?.slots.length || 0)
    );
    if (!renderThroughProductIds.length) return;

    try {
      window.sessionStorage.setItem(key, JSON.stringify({
        version: 2,
        page: currentRecord.page,
        startOffset,
        productIds,
        renderThroughPage,
        renderThroughProductIds,
      }));
    } catch (_error) {
      // Session storage is optional; the initial wishlist batch remains available without it.
    }
  }

  function scheduleWishlistWindowPersist() {
    if (wishlistWindowPersistTimer) return;
    wishlistWindowPersistTimer = window.setTimeout(() => {
      wishlistWindowPersistTimer = null;
      persistWishlistWindow();
    }, 160);
  }

  function flushWishlistWindowPersist() {
    if (wishlistWindowPersistTimer) {
      window.clearTimeout(wishlistWindowPersistTimer);
      wishlistWindowPersistTimer = null;
    }
    persistWishlistWindow();
  }

  function clearWishlistRestoreState(controller) {
    const key = wishlistWindowStorageKey();
    try {
      if (key) window.sessionStorage.removeItem(key);
      const restoreIntentKey = wishlistRestoreIntentKey();
      if (restoreIntentKey) window.sessionStorage.removeItem(restoreIntentKey);
    } catch (_error) {
      // Session storage is optional; the shared controller still clears its in-memory snapshot.
    }
    controller?.clearScrollSnapshot?.();
  }

  function wishlistCardCacheKey(productId) {
    return `${CARD_CACHE_PREFIX}${encodeURIComponent(productId)}`;
  }

  function readCachedWishlistCard(productId) {
    try {
      const markup = window.sessionStorage.getItem(wishlistCardCacheKey(productId));
      if (!markup) return null;

      const template = document.createElement('template');
      template.innerHTML = markup;
      const card = template.content.querySelector('[data-rfa-wishlist-card]');
      if (card?.dataset.productId === productId) return card;

      window.sessionStorage.removeItem(wishlistCardCacheKey(productId));
    } catch (_error) {
      // Card caching is optional; rendering falls back to Shopify when storage is unavailable.
    }
    return null;
  }

  function cacheWishlistCard(productId, card) {
    if (card?.dataset.productId !== productId) return;

    try {
      window.sessionStorage.setItem(wishlistCardCacheKey(productId), card.outerHTML);
    } catch (_error) {
      // Session storage quota is optional; keep the freshly rendered card in memory for this view.
    }
  }

  async function loadRenderedProductCard(productId, product) {
    const cachedCard = readCachedWishlistCard(productId);
    if (cachedCard) return cachedCard;

    const url = new URL(storefrontProductUrl(product), window.location.origin);
    url.searchParams.set('section_id', 'rfa-wishlist-product-card');
    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(`Shopify card rendering failed (${response.status}).`);

    const template = document.createElement('template');
    template.innerHTML = await response.text();
    const card = template.content.querySelector('[data-rfa-wishlist-card]');
    if (!card) throw new Error('Shopify did not return the product-grid card.');
    cacheWishlistCard(productId, card);
    return card;
  }

  function stopWishlistInfiniteLoading() {
    if (!wishlistInfiniteState) return;

    wishlistInfiniteState.controller.removeEventListener(
      'infinite-product-grid:load-next',
      wishlistInfiniteState.loadNext
    );
    wishlistInfiniteState = null;
  }

  async function renderWishlistCards(productIds, productById, existingCards = new Map()) {
    const cards = new Array(productIds.length);
    let nextCardIndex = 0;
    const renderCard = async () => {
      while (nextCardIndex < productIds.length) {
        const cardIndex = nextCardIndex;
        nextCardIndex += 1;
        const productId = productIds[cardIndex];
        const existingCard = existingCards.get(productId);
        cards[cardIndex] = existingCard || await loadRenderedProductCard(productId, productById.get(productId));
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CARD_RENDER_CONCURRENCY, productIds.length) }, renderCard)
    );
    return cards;
  }

  function hasInitialWishlistBatch(state) {
    const expectedIds = state.productIds.slice(0, state.perPage);
    const renderedIds = [...state.grid.querySelectorAll('[data-rfa-wishlist-card][data-product-id]')]
      .map((card) => card.dataset.productId);

    return renderedIds.length === expectedIds.length
      && renderedIds.every((productId, index) => productId === expectedIds[index]);
  }

  async function loadInitialWishlistBatch(state) {
    if (!state.controller.beginClientLoad('initial')) return false;

    const batchIds = state.productIds.slice(0, state.perPage);
    try {
      const productById = await resolveStorefrontProducts(batchIds);
      const missingIds = batchIds.filter((productId) => !productById.has(productId));
      const cards = await renderWishlistCards(
        batchIds.filter((productId) => productById.has(productId)),
        productById
      );
      if (state !== wishlistInfiniteState) return false;

      if (cards.length) {
        state.controller.replaceClientPage(cards, 1, 0);
        state.grid.hidden = false;
        updateButtons(state.grid);
      } else {
        state.grid.hidden = true;
        document.querySelector('[data-rfa-wishlist-empty]').hidden = false;
      }
      state.controller.setClientHasNext(state.nextOffset < state.productIds.length);

      if (missingIds.length) {
        removeMissingProducts(missingIds).catch((cleanupError) => {
          showStatus(cleanupError.message || 'Nicht verfügbare Produkte der Wunschliste konnten nicht bereinigt werden.', true);
        });
      }
      return Boolean(cards.length);
    } catch (error) {
      state.controller.setClientHasNext(false);
      showStatus(error.message || 'Die Wunschliste konnte nicht geladen werden.', true);
      return false;
    } finally {
      state.controller.completeClientLoad();
    }
  }

  async function loadNextWishlistBatch() {
    const state = wishlistInfiniteState;
    if (!state || !state.controller.beginClientLoad()) return;

    const batchIds = state.productIds.slice(state.nextOffset, state.nextOffset + state.perPage);
    if (!batchIds.length) {
      state.controller.setClientHasNext(false);
      state.controller.completeClientLoad();
      return;
    }

    try {
      const productById = await resolveStorefrontProducts(batchIds);
      const missingIds = batchIds.filter((productId) => !productById.has(productId));
      const cards = await renderWishlistCards(
        batchIds.filter((productId) => productById.has(productId)),
        productById
      );
      if (state !== wishlistInfiniteState) return;

      state.controller.appendClientPage(cards, state.nextPage, state.nextOffset);
      state.nextOffset += batchIds.length;
      state.nextPage += 1;
      state.controller.setClientHasNext(state.nextOffset < state.productIds.length);
      updateButtons(state.grid);
      scheduleWishlistWindowPersist();

      if (missingIds.length) {
        removeMissingProducts(missingIds).catch((cleanupError) => {
          showStatus(cleanupError.message || 'Nicht verfügbare Produkte der Wunschliste konnten nicht bereinigt werden.', true);
        });
      }
    } catch (error) {
      state.controller.setClientHasNext(false);
      showStatus(error.message || 'Weitere Produkte der Wunschliste konnten nicht geladen werden.', true);
    } finally {
      state.controller.completeClientLoad();
    }
  }

  async function restoreWishlistWindow(state) {
    const savedWindow = readWishlistWindow(state.productIds, state.perPage);
    if (
      !savedWindow
      || savedWindow.startOffset !== (savedWindow.page - 1) * state.perPage
    ) {
      clearWishlistRestoreState(state.controller);
      return false;
    }

    try {
      const restoredIds = state.productIds.slice(
        0,
        savedWindow.renderThroughStartOffset + savedWindow.renderThroughProductIds.length
      );
      const productById = await resolveStorefrontProducts(restoredIds);
      const missingIds = restoredIds.filter((productId) => !productById.has(productId));
      if (missingIds.length) {
        clearWishlistRestoreState(state.controller);
        removeMissingProducts(missingIds).catch((cleanupError) => {
          showStatus(cleanupError.message || 'Nicht verfügbare Produkte der Wunschliste konnten nicht bereinigt werden.', true);
        });
        return false;
      }

      const cards = await renderWishlistCards(restoredIds, productById);
      const pages = chunks(cards, state.perPage).map((pageCards, index) => ({
        slots: pageCards,
        page: index + 1,
        startIndex: index * state.perPage,
      }));
      if (
        state !== wishlistInfiniteState
        || pages.length !== savedWindow.renderThroughPage
        || !state.controller.replaceClientPages(pages, savedWindow.page)
      ) {
        clearWishlistRestoreState(state.controller);
        return false;
      }

      state.nextPage = pages.length + 1;
      state.nextOffset = restoredIds.length;
      state.controller.setClientHasNext(state.nextOffset < state.productIds.length);
      await state.controller.restoreScrollPosition(state.controller.snapshot);
      updateButtons(state.grid);
      clearWishlistRestoreState(state.controller);
      return true;
    } catch (_error) {
      clearWishlistRestoreState(state.controller);
      return false;
    }
  }

  async function renderWishlistPage() {
    const pageElement = document.querySelector('[data-rfa-wishlist-page]');
    if (!pageElement) return;

    const empty = pageElement.querySelector('[data-rfa-wishlist-empty]');
    const error = pageElement.querySelector('[data-rfa-wishlist-error]');
    const grid = pageElement.querySelector('[data-rfa-wishlist-grid]');
    const count = pageElement.querySelector('[data-rfa-wishlist-count]');
    const perPage = Number.parseInt(pageElement.dataset.productsPerPage, 10) || 24;
    const storedIds = [...savedIds];
    const hadFallbackCards = Boolean(grid.querySelector('[data-rfa-wishlist-card]'));

    error.hidden = true;
    stopWishlistInfiniteLoading();

    try {
      await customElements.whenDefined('infinite-product-grid');
      const controller = pageElement.querySelector('infinite-product-grid');
      if (!controller?.initialized) throw new Error('Die infinite Produktansicht konnte nicht gestartet werden.');

      const firstBatchIds = storedIds.slice(0, perPage);

      grid.hidden = storedIds.length === 0;
      empty.hidden = storedIds.length !== 0;
      count.textContent = String(storedIds.length);
      updateButtons(grid);
      controller.resetClientRecords();
      controller.setClientHasNext(storedIds.length > firstBatchIds.length);
      wishlistInfiniteState = {
        controller,
        grid,
        productIds: storedIds,
        perPage,
        nextOffset: firstBatchIds.length,
        nextPage: 2,
        loadNext: loadNextWishlistBatch,
      };
      controller.addEventListener('infinite-product-grid:load-next', wishlistInfiniteState.loadNext);
      let restored = false;
      try {
        restored = await restoreWishlistWindow(wishlistInfiniteState);
      } catch (restoreError) {
        showStatus(restoreError.message || 'Die vorherige Position der Wunschliste konnte nicht wiederhergestellt werden.', true);
      }
      if (!restored && !hasInitialWishlistBatch(wishlistInfiniteState)) {
        await loadInitialWishlistBatch(wishlistInfiniteState);
      }
      controller.enableScrollPersistence();
    } catch (loadError) {
      grid.hidden = !hadFallbackCards;
      empty.hidden = true;
      error.hidden = false;
      throw loadError;
    }
  }

  function queueMutation(mutation) {
    mutationQueue = mutationQueue
      .then(() => persistMutation(mutation))
      .catch((error) => showStatus(error.message || 'Die Wunschliste konnte nicht aktualisiert werden.', true));
  }

  async function persistMutation(mutation) {
    const { productId, action, restorePosition = null } = mutation;

    try {
      if (!(await getAccessToken())) {
        await beginAuthorization(currentReturnUrl(), mutation);
        return;
      }

      const result = await mutate(productId, action, restorePosition);
      const persistedIds = State.flattenBuckets(result.buckets || result.snapshot.buckets);
      if (result.capacityReached) {
        settleMutation(mutation, persistedIds);
        showStatus('Die Wunschliste ist voll. Es können maximal 1.024 Werke gespeichert werden.', true);
        return;
      }

      settleMutation(mutation, persistedIds);
    } catch (error) {
      if (error.code === 'UNAUTHORIZED') {
        await beginAuthorization(currentReturnUrl(), mutation);
        return;
      }

      settleMutation(mutation);
      if (action === 'remove' && document.querySelector('[data-rfa-wishlist-page]')) {
        renderWishlistPage().catch((renderError) => {
          showStatus(renderError.message || 'Die Produkte der Wunschliste konnten nicht wiederhergestellt werden.', true);
        });
      }
      throw error;
    }
  }

  function handleClick(event) {
    const undoControl = event.target.closest('[data-rfa-wishlist-undo]');
    if (undoControl) {
      event.preventDefault();
      const productId = undoControl.dataset.rfaWishlistProductId;
      if (!productId) return;
      const restorePosition = restoreRemovedWishlistCard(productId);
      if (!restorePosition) return;

      const mutation = { productId, action: 'add', restorePosition };
      pendingMutations.push(mutation);
      publishWishlistState();
      queueMutation(mutation);
      return;
    }

    const control = event.target.closest('[data-rfa-wishlist-control]');
    if (!control) return;
    event.preventDefault();

    if (!config.customerSignedIn) {
      openAccountPrompt();
      return;
    }

    const productId = control.dataset.rfaWishlistProductId;
    if (!productId) {
      window.location.assign(config.wishlistUrl);
      return;
    }

    const mutation = {
      productId,
      action: savedIds.has(productId) ? 'remove' : 'add',
    };
    pendingMutations.push(mutation);
    publishWishlistState();
    if (mutation.action === 'remove' && document.querySelector('[data-rfa-wishlist-page]')) {
      removeWishlistCard(productId);
    }
    queueMutation(mutation);
  }

  async function initialize() {
    if (!config.customerSignedIn) clearAuth();
    window.sessionStorage.removeItem('rfa_wishlist_page_refreshed');
    document.addEventListener('click', handleClick);
    document.addEventListener('click', handleGridNavigation);
    authDialog?.addEventListener('click', handleAccountPromptClick);
    authDialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeAccountPrompt();
    });
    document.addEventListener('shopify:section:load', (event) => updateButtons(event.target));
    document.addEventListener('rfa:product-grid:items-added', (event) => {
      event.detail?.slots?.forEach((slot) => updateButtons(slot));
    });
    window.addEventListener('scroll', scheduleWishlistWindowPersist, { passive: true });
    window.addEventListener('pagehide', flushWishlistWindowPersist);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushWishlistWindowPersist();
    });
    updateButtons(document);

    const handledCallback = await processAuthorizationCallback();
    if (!config.customerSignedIn) return;

    const isWishlistPage = window.location.pathname.replace(/\/$/, '') === new URL(config.wishlistUrl, window.location.origin).pathname;
    if (isWishlistPage && !(await getAccessToken())) {
      if (!handledCallback) await beginAuthorization(currentReturnUrl());
      return;
    }

    if (await getAccessToken()) {
      try {
        await syncFromApi(isWishlistPage);
        if (isWishlistPage) await renderWishlistPage();
      } catch (error) {
        if (error.code !== 'UNAUTHORIZED') showStatus(error.message || 'Die Wunschliste konnte nicht geladen werden.', true);
      }
    }
  }

  initialize().catch((error) => showStatus(error.message || 'Die Wunschliste konnte nicht gestartet werden.', true));
})();
